import { ref, computed } from "vue";
import type { UseFetchOptions } from "nuxt/app";
import { useRuntimeConfig } from "#imports";
import { useLogger } from "./useLogger";
import { isNative } from "../src/utils/platform";
import { useAuthRedirects } from "./useAuthRedirects";

const { getBaseUrl } = useAuthRedirects();

export function useApiService() {
  const logger = useLogger();
  const config = useRuntimeConfig();
  const forceTestVsTestServer = ref(false);
  const shouldUseTestServer = computed(
    () => config.public.isDev && forceTestVsTestServer.value,
  );

  const getApiUrl = (endpoint: string) => {
    const baseUrl = shouldUseTestServer.value
      ? "https://ddebugg-nestor.netlify.app"
      : isNative()
        ? "https://app.usenestor.com"
        : getBaseUrl();
    return `${baseUrl}/api/${endpoint}`;
  };

  const authHeaders = {
    Accept: "application/json",
    "x-nestor-auth": config.public.nestorServerApiSecret,
  };

  /**
   * Check if a response is HTML (indicating an error page)
   */
  const isHtmlResponse = (text: string) => {
    return text.trim().toLowerCase().startsWith('<!doctype html>') || 
           text.trim().toLowerCase().startsWith('<html');
  };

  /**
   * Make a POST request to our API ensuring that the request is a POST and we are authenticated properly
   * Request and response types are passed in to ensure type safety
   * @param endpoint - The endpoint to call
   * @param request - The request body
   * @param serviceName - The name of the service making the call - only used for logging
   * @param options - The options for the fetch call
   * @returns The response from the API
   */
  const makeApiCall = async <TRequest, TResponse>(
    endpoint: string,
    request: TRequest,
    serviceName: string,
    options?: UseFetchOptions<TResponse>,
  ): Promise<TResponse> => {
    try {
      logger.debug(`${serviceName}: Making API call to ${endpoint}`, { request });
      const apiUrl = getApiUrl(endpoint);
      logger.debug(`${serviceName}: API URL: ${apiUrl}`);

      const response = await $fetch.raw(apiUrl, {
        method: "POST",
        body: request,
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        ...options,
      });

      // Check if it's an HTML response (error page)
      const responseText = await response._data;
      if (typeof responseText === 'string' && isHtmlResponse(responseText)) {
        throw new Error(`Endpoint not found or server error: ${endpoint}`);
      }

      return response._data as TResponse;
    } catch (error: any) {
      // If it's already our custom error, throw it as is
      if (error.message?.includes('Endpoint not found')) {
        throw error;
      }
      
      logger.logErrorCaught(`${serviceName}: API call failed`, serviceName, error);
      throw error;
    }
  };

  return {
    forceTestVsTestServer,
    makeApiCall,
  };
} 